import { useEffect, useState } from 'react';
import { G } from './icons.tsx';
import { Ticks } from './Ticks.tsx';
import { VoiceMessage } from './VoiceMessage.tsx';
import type { ChatMessage } from './types.js';
import { useT, type Translate } from '../i18n/I18n.tsx';

/**
 * One message.
 *
 * Handles every content kind the product supports. Attachments are fetched and
 * decrypted only when asked for — a conversation of twenty photos should not
 * pull and decrypt twenty objects on open.
 */

export interface BubbleProps {
  message: ChatMessage;
  mine: boolean;
  /** First of a run by the same sender: carries the tail and extra spacing. */
  groupStart: boolean;
  /** Shown above incoming bubbles when the sender is not implied (groups). */
  showSender: boolean;
  onOpenAttachment: (message: ChatMessage) => Promise<{ url: string; mimeType: string }>;
}

function time(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function bytes(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} kB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

/** Turns bare URLs into links without rendering any user-supplied markup. */
function linkify(text: string): (string | JSX.Element)[] {
  const parts: (string | JSX.Element)[] = [];
  const pattern = /\bhttps?:\/\/[^\s<]+/g;
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    parts.push(
      <a key={match.index} href={match[0]} target="_blank" rel="noreferrer noopener">
        {match[0]}
      </a>,
    );
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

export function Bubble({ message, mine, groupStart, showSender, onOpenAttachment }: BubbleProps) {
  const t = useT();
  const meta = (
    <span className="bub__meta">
      {time(message.createdAt)}
      {mine ? <Ticks state={message.delivery} /> : null}
    </span>
  );

  if (message.unreadable) {
    return (
      <Row mine={mine} groupStart={groupStart}>
        <Tail mine={mine} show={groupStart} />
        <div className="bub__text" style={{ opacity: 0.75, fontStyle: 'italic' }}>
          <G n="lock" size={13} /> {t('message.unreadable')}
          {meta}
        </div>
      </Row>
    );
  }

  if (message.kind === 'text') {
    return (
      <Row mine={mine} groupStart={groupStart}>
        <Tail mine={mine} show={groupStart} />
        {showSender && message.senderName ? (
          <span className="bub__sender">{message.senderName}</span>
        ) : null}
        {message.quoted ? <Quote quoted={message.quoted} /> : null}
        <TextBody message={message} meta={meta} />
      </Row>
    );
  }

  if (message.kind === 'voice') {
    return (
      <Row mine={mine} groupStart={groupStart}>
        <Tail mine={mine} show={groupStart} />
        {showSender && message.senderName ? (
          <span className="bub__sender">{message.senderName}</span>
        ) : null}
        <VoiceMessage message={message} onOpen={onOpenAttachment} />
        <div style={{ height: 2 }}>{meta}</div>
      </Row>
    );
  }

  if (message.kind === 'photo' || message.kind === 'video') {
    return (
      <Row mine={mine} groupStart={groupStart} media>
        <Tail mine={mine} show={groupStart} />
        {showSender && message.senderName ? (
          <span className="bub__sender" style={{ padding: '2px 4px' }}>
            {message.senderName}
          </span>
        ) : null}
        <MediaBody message={message} onOpen={onOpenAttachment} t={t} />
        {message.attachment?.caption ? (
          <div className="bub__caption">
            {message.attachment.caption}
            {meta}
          </div>
        ) : (
          <div style={{ padding: '0 6px' }}>{meta}</div>
        )}
      </Row>
    );
  }

  return (
    <Row mine={mine} groupStart={groupStart}>
      <Tail mine={mine} show={groupStart} />
      {showSender && message.senderName ? (
        <span className="bub__sender">{message.senderName}</span>
      ) : null}
      <DocumentBody message={message} onOpen={onOpenAttachment} t={t} />
      <div>{meta}</div>
    </Row>
  );
}

/* ------------------------------------------------------------------- parts */

function Row({
  mine,
  groupStart,
  media,
  children,
}: {
  mine: boolean;
  groupStart: boolean;
  media?: boolean;
  children: React.ReactNode;
}) {
  const rowClass = [
    'msg-row',
    mine ? 'msg-row--out' : 'msg-row--in',
    groupStart ? 'is-group-start' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={rowClass}>
      <div className={media ? 'bub bub--media' : 'bub'}>{children}</div>
    </div>
  );
}

/** The little corner that makes a bubble point at its sender. */
function Tail({ mine, show }: { mine: boolean; show: boolean }) {
  if (!show) return null;
  return (
    <svg className="bub__tail" viewBox="0 0 8 13" width="8" height="13" aria-hidden="true">
      <path d={mine ? 'M0 0h8v13C8 6 5 1 0 0Z' : 'M8 0H0v13C0 6 3 1 8 0Z'} fill="currentColor" />
    </svg>
  );
}

/**
 * Text, translated where possible.
 *
 * The translation replaces the message outright — no badge, and no way back to
 * the original. A reader should not have to know, or care, which language the
 * other person typed in, and the product-level disclosure that messages are
 * translated on this device lives in Settings rather than on every bubble.
 */
function TextBody({ message, meta }: { message: ChatMessage; meta: JSX.Element }) {
  return (
    <div className="bub__text">
      {linkify(message.translation ?? message.body ?? '')}
      {meta}
    </div>
  );
}

function Quote({ quoted }: { quoted: ChatMessage['quoted'] }) {
  if (!quoted) return null;
  return (
    <div className="quote">
      <div className="quote__body">
        <span className="quote__who">{quoted.author}</span>
        <span className="quote__text">{quoted.preview}</span>
      </div>
    </div>
  );
}

function MediaBody({
  message,
  onOpen,
  t,
}: {
  message: ChatMessage;
  onOpen: BubbleProps['onOpenAttachment'];
  t: Translate;
}) {
  const [media, setMedia] = useState<{ url: string; mimeType: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(
    () => () => {
      if (media) URL.revokeObjectURL(media.url);
    },
    [media],
  );

  if (media) {
    return (
      <span className="bub__media">
        {message.kind === 'photo' ? (
          <img src={media.url} alt={message.attachment?.filename ?? ''} />
        ) : (
          <video src={media.url} controls playsInline />
        )}
      </span>
    );
  }

  return (
    <button
      type="button"
      className="bub__media bub__media--locked"
      disabled={busy}
      onClick={() => {
        setBusy(true);
        setFailed(false);
        onOpen(message)
          .then(setMedia)
          .catch(() => setFailed(true))
          .finally(() => setBusy(false));
      }}
    >
      <G n={message.kind === 'photo' ? 'image' : 'video'} size={28} />
      <span className="bub__filename">{message.attachment?.filename}</span>
      <span>
        {busy
          ? t('message.decrypting')
          : failed
            ? t('message.couldNotOpen')
            : t('message.tapToDecrypt', { size: bytes(message.attachment?.size ?? 0) })}
      </span>
    </button>
  );
}

function DocumentBody({
  message,
  onOpen,
  t,
}: {
  message: ChatMessage;
  onOpen: BubbleProps['onOpenAttachment'];
  t: Translate;
}) {
  const [media, setMedia] = useState<{ url: string; mimeType: string } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(
    () => () => {
      if (media) URL.revokeObjectURL(media.url);
    },
    [media],
  );

  const extension = (message.attachment?.filename.split('.').pop() ?? 'file').slice(0, 5);

  return (
    <div className="doc">
      <span className="doc__icon" aria-hidden="true">
        <G n="document" size={20} />
      </span>
      <span className="doc__body">
        <span className="doc__name">{message.attachment?.filename}</span>
        <span className="doc__meta">
          {extension} · {bytes(message.attachment?.size ?? 0)}
        </span>
      </span>
      {media ? (
        <a
          className="ibtn"
          href={media.url}
          download={message.attachment?.filename}
          title={t('common.save')}
          aria-label={t('message.save', { filename: message.attachment?.filename ?? '' })}
        >
          <G n="download" size={21} />
        </a>
      ) : (
        <button
          type="button"
          className="ibtn"
          disabled={busy}
          title={t('message.decryptAndDownload')}
          aria-label={t('message.decryptAndDownload')}
          onClick={() => {
            setBusy(true);
            onOpen(message)
              .then(setMedia)
              .catch(() => {})
              .finally(() => setBusy(false));
          }}
        >
          <G n={busy ? 'clock' : 'download'} size={21} />
        </button>
      )}
    </div>
  );
}
